import { useState } from 'react'
import { format, formatDistanceToNow } from 'date-fns'
import type { RequestRecord, Verdict } from '../types/analytics'
import { useRequestDetail } from '../api/analytics'
import { useAdminRequestDetail } from '../api/admin'

const verdictColors: Record<Verdict, string> = {
  APPROVED: 'bg-verdict-approved/20 text-verdict-approved px-2 py-1 rounded-sm text-xs font-medium',
  REJECTED: 'bg-verdict-rejected/20 text-verdict-rejected px-2 py-1 rounded-sm text-xs font-medium',
  INCONCLUSIVE: 'bg-verdict-inconclusive/20 text-verdict-inconclusive px-2 py-1 rounded-sm text-xs font-medium',
  ERROR: 'bg-noir-tertiary text-text-secondary px-2 py-1 rounded-sm text-xs font-medium',
}

interface RequestDetailModalProps {
  request: RequestRecord | null
  onClose: () => void
  isAdmin?: boolean
}

export default function RequestDetailModal({
  request,
  onClose,
  isAdmin = false,
}: RequestDetailModalProps) {
  const [showAllReasons, setShowAllReasons] = useState(false)
  const [showVotingDetails, setShowVotingDetails] = useState(false)
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false)

  // Fetch full detail if has_full_content is true
  const { data: userDetail, isLoading: userLoading, error: userError } = useRequestDetail(
    !isAdmin && request?.has_full_content ? request.request_id : null
  )
  const { data: adminDetail, isLoading: adminLoading, error: adminError } = useAdminRequestDetail(
    isAdmin && request?.has_full_content ? request.request_id : null
  )

  const detail = isAdmin ? adminDetail : userDetail
  const isLoadingDetail = isAdmin ? adminLoading : userLoading
  const detailError = isAdmin ? adminError : userError

  if (!request) return null

  // Handle escape key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }

  // Parse summary from reason JSON
  const getSummary = (): string | null => {
    if (!detail?.reason) return null
    try {
      const parsed = JSON.parse(detail.reason)
      return parsed.summary || null
    } catch {
      return detail.reason
    }
  }

  const summary = getSummary()

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-black/60 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />

        <div className="relative transform overflow-hidden rounded-lg bg-noir-secondary border border-border text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="bg-noir-secondary px-4 pb-4 pt-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 id="modal-title" className="text-lg font-semibold text-text-primary font-mono">
                Request Details
              </h3>
              <button
                onClick={onClose}
                className="text-text-secondary hover:text-text-primary focus:outline-hidden focus:ring-2 focus:ring-accent-cyan rounded-sm p-1"
                aria-label="Close modal"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* SECTION 1: Rejection/Approval Reason - HERO CONTENT */}
              {request.has_full_content && (
                <>
                  {isLoadingDetail ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <div
                          className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-accent-cyan border-r-transparent"
                          role="status"
                          aria-label="Loading content"
                        >
                          <span className="sr-only">Loading content...</span>
                        </div>
                        <p className="mt-2 text-sm text-text-secondary">Loading details...</p>
                      </div>
                    </div>
                  ) : detailError ? (
                    <div className="rounded-md bg-verdict-rejected/10 border border-verdict-rejected/30 p-4">
                      <p className="text-sm text-verdict-rejected">
                        Failed to load request details. Please try again.
                      </p>
                    </div>
                  ) : detail ? (
                    <>
                      {/* Rejection Summary */}
                      {request.verdict === 'REJECTED' && (
                        <div className="rounded-lg border border-verdict-rejected/30 bg-verdict-rejected/5 p-4">
                          <h4 className="text-sm font-medium text-verdict-rejected font-mono mb-2 flex items-center gap-2">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Rejection Summary
                          </h4>
                          {summary && (
                            <p className="text-sm text-text-primary mb-3">
                              {summary}
                            </p>
                          )}
                          {detail.fail_reasons && detail.fail_reasons.length > 0 && (
                            <div className="mt-3">
                              <button
                                onClick={() => setShowAllReasons(!showAllReasons)}
                                className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
                              >
                                <svg
                                  className={`h-4 w-4 transition-transform ${showAllReasons ? 'rotate-90' : ''}`}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                {showAllReasons ? 'Hide' : 'Show'} individual voter responses ({detail.fail_reasons.length})
                              </button>
                              {showAllReasons && (
                                <ul className="mt-3 space-y-2 pl-4 border-l-2 border-verdict-rejected/30">
                                  {detail.fail_reasons.map((reason, idx) => (
                                    <li key={idx} className="text-sm text-text-secondary">
                                      {reason}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Approval Reason */}
                      {request.verdict === 'APPROVED' && detail.pass_reasons && detail.pass_reasons.length > 0 && (
                        <div className="rounded-lg border border-verdict-approved/30 bg-verdict-approved/5 p-4">
                          <h4 className="text-sm font-medium text-verdict-approved font-mono mb-2 flex items-center gap-2">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Approval Reason
                          </h4>
                          <p className="text-sm text-text-primary mb-3">
                            {detail.pass_reasons[0]}
                          </p>
                          {detail.pass_reasons.length > 1 && (
                            <div className="mt-3">
                              <button
                                onClick={() => setShowAllReasons(!showAllReasons)}
                                className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
                              >
                                <svg
                                  className={`h-4 w-4 transition-transform ${showAllReasons ? 'rotate-90' : ''}`}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                {showAllReasons ? 'Hide' : 'Show'} alternate approvals ({detail.pass_reasons.length - 1})
                              </button>
                              {showAllReasons && (
                                <ul className="mt-3 space-y-2 pl-4 border-l-2 border-verdict-approved/30">
                                  {detail.pass_reasons.slice(1).map((reason, idx) => (
                                    <li key={idx} className="text-sm text-text-secondary">
                                      {reason}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Inconclusive Summary */}
                      {request.verdict === 'INCONCLUSIVE' && (
                        <div className="rounded-lg border border-verdict-inconclusive/30 bg-verdict-inconclusive/5 p-4">
                          <h4 className="text-sm font-medium text-verdict-inconclusive font-mono mb-2 flex items-center gap-2">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            Inconclusive - Voters Disagreed
                          </h4>
                          {summary && (
                            <p className="text-sm text-text-primary mb-3">
                              {summary}
                            </p>
                          )}
                          {/* Show all voter reasons (both pass and fail) */}
                          {((detail.fail_reasons && detail.fail_reasons.length > 0) ||
                            (detail.pass_reasons && detail.pass_reasons.length > 0)) && (
                            <div className="mt-3">
                              <button
                                onClick={() => setShowAllReasons(!showAllReasons)}
                                className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
                              >
                                <svg
                                  className={`h-4 w-4 transition-transform ${showAllReasons ? 'rotate-90' : ''}`}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                {showAllReasons ? 'Hide' : 'Show'} all voter responses ({(detail.fail_reasons?.length || 0) + (detail.pass_reasons?.length || 0)})
                              </button>
                              {showAllReasons && (
                                <div className="mt-3 space-y-3">
                                  {detail.fail_reasons && detail.fail_reasons.length > 0 && (
                                    <div>
                                      <p className="text-xs text-verdict-rejected font-medium mb-1">Rejected ({detail.fail_reasons.length})</p>
                                      <ul className="space-y-2 pl-4 border-l-2 border-verdict-rejected/30">
                                        {detail.fail_reasons.map((reason, idx) => (
                                          <li key={`fail-${idx}`} className="text-sm text-text-secondary">
                                            {reason}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {detail.pass_reasons && detail.pass_reasons.length > 0 && (
                                    <div>
                                      <p className="text-xs text-verdict-approved font-medium mb-1">Approved ({detail.pass_reasons.length})</p>
                                      <ul className="space-y-2 pl-4 border-l-2 border-verdict-approved/30">
                                        {detail.pass_reasons.map((reason, idx) => (
                                          <li key={`pass-${idx}`} className="text-sm text-text-secondary">
                                            {reason}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Intent - shown for all verdicts */}
                      {detail.extracted_intent && (
                        <p className="text-sm text-text-secondary italic">
                          <span className="font-medium">Intent:</span> {detail.extracted_intent}
                        </p>
                      )}

                      {/* SECTION 2: Code Changes */}
                      {detail.code_content && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-sm font-medium text-text-primary font-mono">Code Changes</h4>
                            {detail.full_file_path && (
                              <span className="text-xs text-text-secondary font-mono bg-noir-tertiary px-2 py-0.5 rounded-sm">
                                {detail.full_file_path.split('/').pop()}
                              </span>
                            )}
                          </div>
                          <DiffView content={detail.code_content} />
                        </div>
                      )}
                    </>
                  ) : null}
                </>
              )}

              {!request.has_full_content && (
                <div className="rounded-md bg-noir-tertiary border border-border p-4">
                  <div className="flex">
                    <div className="shrink-0">
                      <svg
                        className="h-5 w-5 text-text-secondary"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-text-primary">Full content not available</h3>
                      <p className="mt-2 text-sm text-text-secondary">
                        Only metadata is available for this request. Some older records may not include full content.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 3: Request Overview - Compact card */}
              <div className="rounded-lg border border-border bg-noir-tertiary p-4">
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className={verdictColors[request.verdict]}>
                    {request.verdict}
                  </span>
                  <span className="text-text-secondary">
                    {request.plan_tier}
                  </span>
                  <span className="text-text-secondary">
                    {formatDistanceToNow(new Date(request.timestamp), { addSuffix: true })}
                  </span>
                  <span className="text-text-secondary font-mono text-xs">
                    {request.request_id.slice(0, 8)}...
                  </span>
                  {request.file_extension && (
                    <span className="text-text-secondary">
                      .{request.file_extension} • {request.content_lines} lines
                    </span>
                  )}
                </div>
              </div>

              {/* SECTION 4: How We Decided - Collapsible */}
              <CollapsibleSection
                title="How We Decided"
                isOpen={showVotingDetails}
                onToggle={() => setShowVotingDetails(!showVotingDetails)}
              >
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <Field label="Voting Rounds" value={detail?.total_voting_rounds ?? request.total_voting_rounds} />
                  <Field label="Pass Votes" value={detail?.pass_votes ?? request.pass_votes} />
                  <Field label="Fail Votes" value={detail?.fail_votes ?? request.fail_votes} />
                  <Field label="Discarded" value={detail?.discarded_votes ?? request.discarded_votes} />
                  <Field label="Total Duration" value={`${((detail?.total_duration_ms ?? request.total_duration_ms) / 1000).toFixed(2)}s`} />
                  <Field label="Voting Time" value={`${((detail?.voting_duration_ms ?? request.voting_duration_ms) / 1000).toFixed(2)}s`} />
                  {/* Admin-only: Provider details */}
                  {isAdmin && (
                    <>
                      <Field label="Groq Calls" value={detail?.groq_calls ?? request.groq_calls} />
                      <Field label="OpenRouter Calls" value={detail?.openrouter_calls ?? request.openrouter_calls} />
                    </>
                  )}
                </div>
              </CollapsibleSection>

              {/* SECTION 6: Technical Details - Collapsible */}
              <CollapsibleSection
                title="Technical Details"
                isOpen={showTechnicalDetails}
                onToggle={() => setShowTechnicalDetails(!showTechnicalDetails)}
              >
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <Field label="Request ID" value={request.request_id} />
                  <Field label="Timestamp" value={format(new Date(request.timestamp), 'PPpp')} />
                  <Field label="File Extension" value={request.file_extension || '-'} />
                  <Field label="Lines" value={request.content_lines} />
                  <Field label="Characters" value={(detail?.content_chars ?? request.content_chars).toLocaleString()} />
                  <Field label="Client Version" value={detail?.client_version || request.client_version || '-'} />
                  <Field label="Editor" value={detail?.editor_type || request.editor_type || '-'} />
                  <Field label="Summarization" value={`${((detail?.summarization_ms ?? request.summarization_ms) / 1000).toFixed(2)}s`} />
                  {/* Admin-only fields */}
                  {isAdmin && (
                    <>
                      <Field label="HTTP Status" value={request.http_status} />
                      <Field label="Intent Extraction" value={`${(request.intent_extraction_ms / 1000).toFixed(2)}s`} />
                    </>
                  )}
                  {detail?.full_file_path && (
                    <>
                      <span className="text-text-secondary">File Path</span>
                      <span className="text-text-primary font-mono text-xs break-all">{detail.full_file_path}</span>
                    </>
                  )}
                </div>
              </CollapsibleSection>
            </div>
          </div>

          <div className="bg-noir-tertiary px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button onClick={onClose} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function CollapsibleSection({
  title,
  isOpen,
  onToggle,
  children,
}: {
  title: string
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 bg-noir-tertiary hover:bg-noir-tertiary/80 transition-colors"
      >
        <h4 className="text-sm font-medium text-text-primary font-mono">{title}</h4>
        <svg
          className={`h-4 w-4 text-text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="p-3 bg-noir-secondary">
          {children}
        </div>
      )}
    </div>
  )
}

function Field({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <>
      <span className="text-text-secondary">{label}</span>
      <span className="text-text-primary">{value}</span>
    </>
  )
}

// Parse code content into diff sections for syntax highlighting
function parseCodeContent(content: string): { type: 'edit' | 'write' | 'plain'; oldCode?: string; newCode?: string; content?: string }[] {
  const sections: { type: 'edit' | 'write' | 'plain'; oldCode?: string; newCode?: string; content?: string }[] = []

  // Split by EDIT: or WRITE: markers
  const editPattern = /EDIT:\s*Replace:\s*([\s\S]*?)\s*With:\s*([\s\S]*?)(?=(?:EDIT:|WRITE:|$))/g
  const writePattern = /WRITE:\s*([\s\S]*?)(?=(?:EDIT:|WRITE:|$))/g

  let lastIndex = 0
  let match

  // First, find all EDIT blocks
  const allMatches: { index: number; length: number; type: 'edit' | 'write'; oldCode?: string; newCode?: string; content?: string }[] = []

  while ((match = editPattern.exec(content)) !== null) {
    allMatches.push({
      index: match.index,
      length: match[0].length,
      type: 'edit',
      oldCode: match[1].trim(),
      newCode: match[2].trim()
    })
  }

  // Then find all WRITE blocks
  while ((match = writePattern.exec(content)) !== null) {
    // Skip if this overlaps with an EDIT block
    const overlaps = allMatches.some(m =>
      m.type === 'edit' &&
      match!.index >= m.index &&
      match!.index < m.index + m.length
    )
    if (!overlaps) {
      allMatches.push({
        index: match.index,
        length: match[0].length,
        type: 'write',
        content: match[1].trim()
      })
    }
  }

  // Sort by index
  allMatches.sort((a, b) => a.index - b.index)

  // Build sections with plain text between matches
  for (const m of allMatches) {
    if (m.index > lastIndex) {
      const plainText = content.slice(lastIndex, m.index).trim()
      if (plainText) {
        sections.push({ type: 'plain', content: plainText })
      }
    }

    if (m.type === 'edit') {
      sections.push({ type: 'edit', oldCode: m.oldCode, newCode: m.newCode })
    } else {
      sections.push({ type: 'write', content: m.content })
    }

    lastIndex = m.index + m.length
  }

  // Handle remaining content
  if (lastIndex < content.length) {
    const remaining = content.slice(lastIndex).trim()
    if (remaining) {
      sections.push({ type: 'plain', content: remaining })
    }
  }

  // If no patterns matched, return as plain
  if (sections.length === 0) {
    sections.push({ type: 'plain', content })
  }

  return sections
}

function DiffView({ content }: { content: string }) {
  const sections = parseCodeContent(content)

  return (
    <div className="rounded-sm overflow-hidden border border-border max-h-96 overflow-y-auto">
      {sections.map((section, idx) => (
        <div key={idx}>
          {section.type === 'edit' && (
            <>
              {/* Old code - red background */}
              <div className="bg-verdict-rejected/15 border-l-4 border-verdict-rejected">
                <div className="px-3 py-1 text-xs text-verdict-rejected font-medium border-b border-verdict-rejected/30">
                  − Removed
                </div>
                <pre className="text-xs text-text-primary p-3 overflow-x-auto whitespace-pre-wrap">
                  {section.oldCode}
                </pre>
              </div>
              {/* New code - green background */}
              <div className="bg-verdict-approved/15 border-l-4 border-verdict-approved">
                <div className="px-3 py-1 text-xs text-verdict-approved font-medium border-b border-verdict-approved/30">
                  + Added
                </div>
                <pre className="text-xs text-text-primary p-3 overflow-x-auto whitespace-pre-wrap">
                  {section.newCode}
                </pre>
              </div>
            </>
          )}
          {section.type === 'write' && (
            <div className="bg-verdict-approved/15 border-l-4 border-verdict-approved">
              <div className="px-3 py-1 text-xs text-verdict-approved font-medium border-b border-verdict-approved/30">
                + New File
              </div>
              <pre className="text-xs text-text-primary p-3 overflow-x-auto whitespace-pre-wrap">
                {section.content}
              </pre>
            </div>
          )}
          {section.type === 'plain' && (
            <pre className="text-xs text-text-primary bg-noir-tertiary p-3 overflow-x-auto whitespace-pre-wrap">
              {section.content}
            </pre>
          )}
        </div>
      ))}
    </div>
  )
}
